import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2, Store, Plus, Trash2 } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const staffFavoriteSchema = z.object({
  staffName: z.string().trim().min(1, "Staff name is required").max(200),
  bookTitle: z.string().trim().min(1, "Book title is required").max(200),
  bookAuthor: z.string().trim().min(1, "Author is required").max(200),
  genre: z.string().trim().min(1, "Genre is required").max(100),
  whyILoveIt: z.string().trim().min(10, "Tell us more about why you love it").max(2000),
  coverUrl: z.string().trim().url("Please enter a valid URL").max(500).or(z.literal("")).optional(),
  purchaseUrl: z.string().trim().url("Please enter a valid URL").max(500).or(z.literal("")).optional(),
});

const storeSchema = z.object({
  storeName: z.string().trim().min(1, "Store name is required").max(200),
  email: z.string().trim().email("Please enter a valid email").max(255),
  phone: z.string().trim().max(30).optional(),
  city: z.string().trim().min(1, "City is required").max(100),
  state: z.string().trim().min(1, "State is required").max(100),
  address: z.string().trim().max(300).optional(),
  websiteUrl: z.string().trim().url("Please enter a valid URL").max(500).or(z.literal("")).optional(),
  description: z.string().trim().min(10, "Please provide a longer description").max(2000),
});

type StaffFavorite = z.infer<typeof staffFavoriteSchema>;
type StoreForm = z.infer<typeof storeSchema>;

const emptyFavorite: StaffFavorite = {
  staffName: "", bookTitle: "", bookAuthor: "", genre: "", whyILoveIt: "", coverUrl: "", purchaseUrl: "",
};

export default function PartnerBookstore() {
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hp, setHp] = useState(""); // honeypot field
  const [form, setForm] = useState<StoreForm>({
    storeName: "", email: "", phone: "", city: "", state: "", address: "", websiteUrl: "", description: "",
  });
  const [favorites, setFavorites] = useState<StaffFavorite[]>([{ ...emptyFavorite }]);

  const updateStore = (field: keyof StoreForm, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const updateFavorite = (index: number, field: keyof StaffFavorite, value: string) => {
    setFavorites((prev) => prev.map((f, i) => (i === index ? { ...f, [field]: value } : f)));
  };

  const addFavorite = () => {
    if (favorites.length >= 10) { toast.info("Maximum 10 staff favorites per submission."); return; }
    setFavorites((prev) => [...prev, { ...emptyFavorite }]);
  };

  const removeFavorite = (index: number) => {
    if (favorites.length <= 1) return;
    setFavorites((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check — bots fill hidden fields
    if (hp) { setSubmitted(true); return; }

    const storeResult = storeSchema.safeParse(form);
    if (!storeResult.success) {
      toast.error(storeResult.error.errors[0]?.message || "Please check store details");
      return;
    }

    for (let i = 0; i < favorites.length; i++) {
      const favResult = staffFavoriteSchema.safeParse(favorites[i]);
      if (!favResult.success) {
        toast.error(`Staff Pick #${i + 1}: ${favResult.error.errors[0]?.message}`);
        return;
      }
    }

    setSaving(true);

    // Insert bookstore
    const { data: storeData, error: storeError } = await supabase
      .from("bookstore_submissions" as any)
      .insert({
        store_name: storeResult.data.storeName,
        email: storeResult.data.email,
        phone: storeResult.data.phone || null,
        city: storeResult.data.city,
        state: storeResult.data.state,
        address: storeResult.data.address || null,
        website_url: storeResult.data.websiteUrl || null,
        description: storeResult.data.description,
      } as any)
      .select("id")
      .maybeSingle();

    if (storeError || !storeData) {
      toast.error("Failed to submit store. Please try again.");
      setSaving(false);
      return;
    }

    // Insert staff favorites linked to the bookstore
    const favRows = favorites.map((f) => ({
      bookstore_id: (storeData as any).id,
      staff_name: f.staffName,
      book_title: f.bookTitle,
      book_author: f.bookAuthor,
      genre: f.genre,
      why_i_love_it: f.whyILoveIt,
      cover_url: f.coverUrl || null,
      purchase_url: f.purchaseUrl || null,
    }));

    const { error: favError } = await supabase.from("staff_favorites" as any).insert(favRows as any);

    setSaving(false);

    if (favError) {
      toast.error("Store submitted but staff picks failed. Please contact us.");
      return;
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="max-w-md text-center">
          <CheckCircle2 className="mx-auto mb-4 text-primary" size={48} />
          <h1 className="mb-2 font-display text-3xl font-extrabold text-foreground">Welcome, Partner!</h1>
          <p className="mb-6 text-muted-foreground">
            Your bookstore has been submitted for review. Once approved, your staff favorites will appear
            on our Staff Favorites wall — and local readers will discover your store through our recommendations.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setForm({ storeName: "", email: "", phone: "", city: "", state: "", address: "", websiteUrl: "", description: "" });
              setFavorites([{ ...emptyFavorite }]);
            }}
            variant="outline"
            className="rounded-xl"
          >
            Submit Another Store
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-2xl px-4 py-12">
        {/* Hero blurb */}
        <div className="mb-10 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
            <Store className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Bookstore Partnership
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Are you a local or independent bookstore? Partner with us and we'll connect readers in your
            area to your <strong>staff favorites</strong>. Think of it as your in-store picks wall — but reaching
            thousands of readers who trust personal, hand-picked recommendations.
          </p>
        </div>

        {/* How it works */}
        <div className="mb-10 rounded-2xl border border-border/50 bg-card p-6">
          <h2 className="mb-3 font-display text-lg font-bold text-foreground">How it works</h2>
          <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
            <li>Tell us about your store and submit your team's favorite reads below.</li>
            <li>We feature your staff picks on our <strong>Staff Favorites</strong> wall with your store's name.</li>
            <li>Readers discover your store through the books your team genuinely loves.</li>
            <li>Everyone wins: readers find great books, you find new customers.</li>
          </ol>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Store details */}
          <div>
            <h2 className="mb-4 font-display text-xl font-bold text-foreground">Store Details</h2>
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="bs-name">Store Name *</Label>
                  <Input id="bs-name" value={form.storeName} onChange={(e) => updateStore("storeName", e.target.value)} placeholder="The Corner Bookshop" className="rounded-xl" maxLength={200} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bs-email">Contact Email *</Label>
                  <Input id="bs-email" type="email" value={form.email} onChange={(e) => updateStore("email", e.target.value)} placeholder="hello@yourbookstore.com" className="rounded-xl" maxLength={255} />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="bs-city">City *</Label>
                  <Input id="bs-city" value={form.city} onChange={(e) => updateStore("city", e.target.value)} placeholder="Portland" className="rounded-xl" maxLength={100} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bs-state">State *</Label>
                  <Input id="bs-state" value={form.state} onChange={(e) => updateStore("state", e.target.value)} placeholder="OR" className="rounded-xl" maxLength={100} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bs-phone">Phone</Label>
                  <Input id="bs-phone" value={form.phone} onChange={(e) => updateStore("phone", e.target.value)} placeholder="(503) 555-1234" className="rounded-xl" maxLength={30} />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="bs-address">Street Address</Label>
                  <Input id="bs-address" value={form.address} onChange={(e) => updateStore("address", e.target.value)} placeholder="123 Main St" className="rounded-xl" maxLength={300} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bs-website">Website</Label>
                  <Input id="bs-website" value={form.websiteUrl} onChange={(e) => updateStore("websiteUrl", e.target.value)} placeholder="https://yourbookstore.com" className="rounded-xl" maxLength={500} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bs-desc">About Your Store *</Label>
                <Textarea
                  id="bs-desc"
                  value={form.description}
                  onChange={(e) => updateStore("description", e.target.value)}
                  placeholder="Tell us what makes your bookstore special — your history, community, and what readers love about your space."
                  className="min-h-[100px] rounded-xl"
                  maxLength={2000}
                />
              </div>
            </div>
          </div>

          {/* Staff Favorites */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-foreground">Staff Favorites</h2>
              <Button type="button" variant="outline" size="sm" onClick={addFavorite} className="rounded-xl">
                <Plus className="mr-1 h-4 w-4" /> Add Pick
              </Button>
            </div>
            <p className="mb-6 text-sm text-muted-foreground">
              Add your team's go-to book picks. Each one will appear on our Staff Favorites wall with their name and why they love it.
            </p>

            <div className="space-y-6">
              {favorites.map((fav, i) => (
                <div key={i} className="rounded-2xl border border-border/50 bg-card p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-display text-sm font-bold text-muted-foreground">Staff Pick #{i + 1}</span>
                    {favorites.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeFavorite(i)} className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Staff Member Name *</Label>
                        <Input value={fav.staffName} onChange={(e) => updateFavorite(i, "staffName", e.target.value)} placeholder="Sarah" className="rounded-xl" maxLength={200} />
                      </div>
                      <div className="space-y-2">
                        <Label>Genre *</Label>
                        <Input value={fav.genre} onChange={(e) => updateFavorite(i, "genre", e.target.value)} placeholder="e.g. Literary Fiction" className="rounded-xl" maxLength={100} />
                      </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Book Title *</Label>
                        <Input value={fav.bookTitle} onChange={(e) => updateFavorite(i, "bookTitle", e.target.value)} placeholder="The Great Novel" className="rounded-xl" maxLength={200} />
                      </div>
                      <div className="space-y-2">
                        <Label>Author *</Label>
                        <Input value={fav.bookAuthor} onChange={(e) => updateFavorite(i, "bookAuthor", e.target.value)} placeholder="Author Name" className="rounded-xl" maxLength={200} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Why I Love It *</Label>
                      <Textarea
                        value={fav.whyILoveIt}
                        onChange={(e) => updateFavorite(i, "whyILoveIt", e.target.value)}
                        placeholder="This is the personal touch — tell readers why this book stayed with you."
                        className="min-h-[80px] rounded-xl"
                        maxLength={2000}
                      />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Cover Image URL</Label>
                        <Input value={fav.coverUrl} onChange={(e) => updateFavorite(i, "coverUrl", e.target.value)} placeholder="https://..." className="rounded-xl" maxLength={500} />
                      </div>
                      <div className="space-y-2">
                        <Label>Purchase Link</Label>
                        <Input value={fav.purchaseUrl} onChange={(e) => updateFavorite(i, "purchaseUrl", e.target.value)} placeholder="https://..." className="rounded-xl" maxLength={500} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Honeypot — hidden from real users */}
          <div aria-hidden="true" className="absolute left-[-9999px]">
            <label htmlFor="bs_website_confirm">Leave blank</label>
            <input id="bs_website_confirm" name="bs_website_confirm" type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
          </div>

          <Button type="submit" size="lg" className="w-full rounded-xl font-semibold" disabled={saving}>
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</> : "Submit Partnership Application"}
          </Button>
        </form>
      </div>
    </div>
  );
}
